import { useState, useEffect } from 'react';
import {
  Newspaper, Filter, ExternalLink, Sparkles, RefreshCw, Calendar,
  Tag, Building2, AlertCircle, FileText, Pill, Syringe, Briefcase, CreditCard, UserCheck,
  ChevronDown, ChevronUp, Zap, Check, Clock, Info as InfoIcon, Plus,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  reglementaire: { label: 'R\u00e9glementaire', color: '#1B2A4A', icon: FileText },
  remuneration:  { label: 'R\u00e9mun\u00e9ration',  color: '#B8860B', icon: CreditCard },
  vaccination:   { label: 'Vaccination',   color: '#059669', icon: Syringe },
  produits:      { label: 'Produits',      color: '#DC2626', icon: Pill },
  metier:        { label: 'M\u00e9tier',        color: '#7C3AED', icon: Briefcase },
  tiers_payant:  { label: 'Tiers payant',  color: '#0EA5E9', icon: CreditCard },
  patient:       { label: 'Patient',       color: '#EC4899', icon: UserCheck },
  general:       { label: 'G\u00e9n\u00e9ral',       color: '#64748B', icon: Tag },
};

const PRIORITY_META = {
  urgent:      { label: 'Action urgente',      stripe: '#DC2626', bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    chip: 'bg-red-600 text-white',    icon: Zap },
  recommande:  { label: 'Action recommand\u00e9e', stripe: '#F59E0B', bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  chip: 'bg-amber-500 text-white',  icon: Clock },
  informatif:  { label: 'Informatif',          stripe: '#3B82F6', bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   chip: 'bg-blue-500 text-white',   icon: InfoIcon },
};

const SOURCE_META = {
  fspf:      { label: 'FSPF',      kind: 'Syndicat' },
  uspo:      { label: 'USPO',      kind: 'Syndicat' },
  quotidien: { label: 'Le Quotidien du Pharmacien', kind: 'Presse' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch (e) {
    return '';
  }
}

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days < 1) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days}j`;
    if (days < 30) return `Il y a ${Math.floor(days / 7)}sem.`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch (e) {
    return '';
  }
}

function ArticleCard({ article, onGenerateActions, generating, actions, onMarkRead, onImportActions, importing }) {
  const [expanded, setExpanded] = useState(false);
  const primaryCat = article.categories?.[0] || 'general';
  const meta = CATEGORY_META[primaryCat] || CATEGORY_META.general;
  const Icon = meta.icon;
  const src = SOURCE_META[article.source_id];

  const priority = actions?.priority || article.priority;
  const actionList = actions?.actions || article.actions;
  const priorityMeta = priority ? PRIORITY_META[priority] : null;
  const PrioIcon = priorityMeta?.icon || InfoIcon;
  const isRead = article.is_read;

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!expanded && !actionList && !generating) {
      onGenerateActions?.(article.id);
    }
    if (!isRead) onMarkRead?.(article.id);
    setExpanded(!expanded);
  };

  return (
    <div
      className={`bg-white rounded-xl border ${priorityMeta ? priorityMeta.border : 'border-gray-100'} overflow-hidden transition ${isRead ? 'opacity-70' : ''}`}
      data-testid={`article-${article.id}`}
      style={priorityMeta ? { borderLeft: `4px solid ${priorityMeta.stripe}` } : {}}
    >
      <div className="p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 relative" style={{ backgroundColor: `${meta.color}15` }}>
          <Icon size={16} style={{ color: meta.color }} />
          {!isRead && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#B8860B] border-2 border-white" data-testid={`unread-${article.id}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {priorityMeta && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase flex items-center gap-1 ${priorityMeta.chip}`}>
                <PrioIcon size={9} /> {priorityMeta.label}
              </span>
            )}
            {article.categories?.slice(0, 2).map(c => {
              const m = CATEGORY_META[c] || CATEGORY_META.general;
              return (
                <span key={c} className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                      style={{ backgroundColor: `${m.color}15`, color: m.color }}>
                  {m.label}
                </span>
              );
            })}
            <span className="text-[9px] text-gray-400 font-semibold">
              {src?.label || article.source_name} · {fmtRelative(article.published_at)}
            </span>
            {!isRead && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#B8860B] text-white font-bold uppercase">Nouveau</span>}
          </div>
          <h3 className={`text-sm font-bold ${isRead ? 'text-gray-600' : 'text-[#1B2A4A]'}`}>{article.title}</h3>
          {article.summary && (
            <p className={`text-[11px] text-gray-500 mt-1 leading-relaxed ${expanded ? '' : 'line-clamp-2'}`}>
              {article.summary}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={handleToggle}
              className="text-[10px] font-bold text-[#1B2A4A] hover:text-[#B8860B] flex items-center gap-1"
              data-testid={`actions-toggle-${article.id}`}
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {actionList ? "Actions recommand\u00e9es" : "G\u00e9n\u00e9rer actions IA"}
            </button>
            <a href={article.link} target="_blank" rel="noopener noreferrer"
               className="text-[10px] font-bold text-gray-400 hover:text-[#B8860B] flex items-center gap-1"
               onClick={(e) => { e.stopPropagation(); if (!isRead) onMarkRead?.(article.id); }}>
              <ExternalLink size={11} /> Source
            </a>
          </div>
        </div>
      </div>

      {expanded && (
        <div className={`${priorityMeta?.bg || 'bg-gray-50'} px-4 pb-4 pt-3 border-t border-gray-100`}>
          {generating && !actionList && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              <RefreshCw size={11} className="animate-spin" /> Génération des actions...
            </div>
          )}
          {actionList && (
            <div data-testid={`actions-${article.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                  <Zap size={10} /> {actionList.length} {"action(s) recommand\u00e9e(s)"}
                </div>
                <button
                  onClick={() => onImportActions?.(article.id)}
                  disabled={importing}
                  className="text-[10px] flex items-center gap-1 px-2 py-1 rounded bg-[#1B2A4A] text-white font-bold disabled:opacity-50"
                  data-testid={`import-actions-${article.id}`}
                >
                  <Plus size={10} /> {importing ? "..." : "Ajouter \u00e0 mes actions"}
                </button>
              </div>
              <div className="space-y-2">
                {actionList.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-white rounded border border-gray-100">
                    <Check size={11} className={`mt-0.5 shrink-0 ${priority === 'urgent' ? 'text-red-500' : priority === 'recommande' ? 'text-amber-500' : 'text-blue-500'}`} />
                    <div className="flex-1">
                      <p className="text-[12px] text-[#1B2A4A] leading-relaxed">{a.text}</p>
                      {a.delay_days != null && (
                        <p className="text-[10px] text-gray-400 mt-0.5">D&eacute;lai conseill&eacute; : sous {a.delay_days}j</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!generating && !actionList && (
            <p className="text-[11px] text-gray-400">{"Cliquez pour g\u00e9n\u00e9rer les actions IA."}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DigestPanel({ digest, loading, onRefresh }) {
  return (
    <div className="bg-gradient-to-br from-[#1B2A4A] to-[#2C3E5C] rounded-xl p-5 text-white relative overflow-hidden" data-testid="veille-digest">
      <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-20 translate-x-20" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#B8860B]" />
            <h3 className="text-sm font-bold uppercase tracking-wider">Digest IA hebdomadaire</h3>
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
            data-testid="veille-digest-refresh-btn"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> {loading ? 'En cours' : 'Actualiser'}
          </button>
        </div>
        {!digest && !loading && (
          <p className="text-xs text-white/60">Cliquez sur Actualiser pour g&eacute;n&eacute;rer le digest hebdomadaire.</p>
        )}
        {loading && !digest && (
          <p className="text-xs text-white/60">Génération en cours...</p>
        )}
        {digest && (
          <div className="text-xs leading-relaxed text-white/90 whitespace-pre-line" data-testid="veille-digest-content">
            {digest.digest}
          </div>
        )}
        {digest?.top_articles?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-[10px] uppercase font-bold text-white/60 mb-2">Articles cl&eacute;s</p>
            <div className="space-y-1.5">
              {digest.top_articles.slice(0, 5).map((a, i) => (
                <a key={i} href={a.link} target="_blank" rel="noopener noreferrer"
                   className="block text-[11px] text-white/80 hover:text-[#B8860B] transition">
                  · {a.title}
                </a>
              ))}
            </div>
          </div>
        )}
        {digest?.generated_at && (
          <p className="text-[9px] text-white/40 mt-3 uppercase">G&eacute;n&eacute;r&eacute; le {new Date(digest.generated_at).toLocaleDateString('fr-FR')}</p>
        )}
      </div>
    </div>
  );
}

export default function VeilleTab() {
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestLoading, setDigestLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [tab, setTab] = useState('feed');
  const [actionsByArticle, setActionsByArticle] = useState({});
  const [generatingId, setGeneratingId] = useState(null);
  const [importingId, setImportingId] = useState(null);

  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}` };

  const generateActions = async (articleId) => {
    setGeneratingId(articleId);
    try {
      const res = await fetch(`${API_URL}/veille/article/${articleId}/actions`, { headers });
      if (res.ok) {
        const json = await res.json();
        setActionsByArticle(prev => ({ ...prev, [articleId]: json }));
      }
    } catch (e) {
      console.error(e);
    }
    setGeneratingId(null);
  };

  const markRead = async (articleId) => {
    try {
      await fetch(`${API_URL}/veille/article/${articleId}/read`, { method: 'POST', headers });
      setData(prev => prev ? {
        ...prev,
        articles: prev.articles.map(a => a.id === articleId ? { ...a, is_read: true } : a),
        new_since_last_visit: Math.max(0, (prev.new_since_last_visit || 1) - 1),
      } : prev);
    } catch (e) { console.error(e); }
  };

  const importActions = async (articleId) => {
    setImportingId(articleId);
    try {
      const res = await fetch(`${API_URL}/actions/import-from-veille/${articleId}`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        const j = await res.json();
        alert(`${j.created} action(s) ajout\u00e9e(s) \u00e0 'Mes actions'`);
      }
    } catch (e) { console.error(e); }
    setImportingId(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.set('category', filterCategory);
      if (filterSource !== 'all') params.set('source_id', filterSource);
      params.set('limit', '40');
      const res = await fetch(`${API_URL}/veille/news?${params}`, { headers });
      const json = await res.json();
      setData(json);
      const dRes = await fetch(`${API_URL}/veille/digest`, { headers });
      if (dRes.ok) setDigest(await dRes.json());
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const refreshDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await fetch(`${API_URL}/veille/digest?refresh=true`, { headers });
      setDigest(await res.json());
    } catch (e) {
      console.error(e);
    }
    setDigestLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCategory, filterSource]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="veille-loading">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const articles = data.articles || [];
  const cats = data.categories || [];
  const srcs = data.sources || [];

  return (
    <div className="space-y-4" data-testid="veille-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Newspaper size={14} className="text-[#1B2A4A]" />
            <span className="text-xs font-bold text-[#1B2A4A]">Veille r&eacute;glementaire & m&eacute;tier</span>
            {data.new_since_last_visit > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#B8860B] text-white font-bold uppercase" data-testid="new-badge">
                {data.new_since_last_visit} nouveau{data.new_since_last_visit > 1 ? 'x' : ''}
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Sources: {srcs.map(s => s.name).join(' \u00b7 ')}
            {data.last_refresh && ' \u00b7 MAJ ' + new Date(data.last_refresh).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { k: 'feed', l: `Flux (${articles.length})` },
            { k: 'digest', l: 'Digest IA' },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition ${tab === t.k ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500'}`}
              data-testid={`veille-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {tab === 'feed' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Filter size={12} className="text-gray-400" />
              <span className="text-[10px] font-bold text-gray-500 uppercase">Cat&eacute;gories</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-2 py-1 rounded-full text-[10px] font-bold transition ${filterCategory === 'all' ? 'bg-[#1B2A4A] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                data-testid="filter-cat-all"
              >
                Toutes ({Object.values(data.by_category || {}).reduce((s, c) => s + c, 0)})
              </button>
              {cats.map(c => {
                const meta = CATEGORY_META[c] || CATEGORY_META.general;
                const count = data.by_category?.[c] || 0;
                if (count === 0) return null;
                return (
                  <button
                    key={c}
                    onClick={() => setFilterCategory(c)}
                    className={`px-2 py-1 rounded-full text-[10px] font-bold transition ${filterCategory === c ? 'text-white' : 'text-gray-500 hover:opacity-80'}`}
                    style={filterCategory === c ? { backgroundColor: meta.color } : { backgroundColor: `${meta.color}15`, color: meta.color }}
                    data-testid={`filter-cat-${c}`}
                  >
                    {meta.label} ({count})
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 mt-3 mb-2">
              <Building2 size={12} className="text-gray-400" />
              <span className="text-[10px] font-bold text-gray-500 uppercase">Sources</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterSource('all')}
                className={`px-2 py-1 rounded-full text-[10px] font-bold transition ${filterSource === 'all' ? 'bg-[#1B2A4A] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                data-testid="filter-src-all"
              >
                Toutes
              </button>
              {srcs.map(s => {
                const count = data.by_source?.[s.id] || 0;
                if (count === 0) return null;
                return (
                  <button
                    key={s.id}
                    onClick={() => setFilterSource(s.id)}
                    className={`px-2 py-1 rounded-full text-[10px] font-bold transition ${filterSource === s.id ? 'bg-[#1B2A4A] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    data-testid={`filter-src-${s.id}`}
                  >
                    {SOURCE_META[s.id]?.label || s.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Articles */}
          <div className="space-y-2" data-testid="articles-list">
            {articles.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <AlertCircle size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Aucun article ne correspond aux filtres.</p>
              </div>
            ) : articles.map(a => (
              <ArticleCard
                key={a.id}
                article={a}
                onGenerateActions={generateActions}
                generating={generatingId === a.id}
                actions={actionsByArticle[a.id]}
                onMarkRead={markRead}
                onImportActions={importActions}
                importing={importingId === a.id}
              />
            ))}
          </div>
        </>
      )}

      {tab === 'digest' && (
        <DigestPanel digest={digest} loading={digestLoading} onRefresh={refreshDigest} />
      )}
    </div>
  );
}
